import { useEffect } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams } from 'react-router';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';

import {
  FormLayout,
  FormSection,
  SectionHeader,
  RadioField,
  ToggleWithDescription,
  SectionParagraph
} from './tracking-details-form/base';
import { useToast } from '@/hooks/use-toast';
import { MultiSelect } from '@/routes/trackings/components/multi-select-dropdown';
import {
  SALES_CHANNEL_OPTIONS,
  SUBSCRIPTION_SALES_CHANNELS,
  SUBSCRIPTION_ORDER_TAGS,
  ORDER_TAG_OPTIONS
} from '@/routes/trackings/tracking-constants';
import { SaveButton } from './save-button';
import { useIntegrationStore } from '../stores/tracking-store';
import { useApolloClient } from '@apollo/client';

const transactionsFormSchema = z.object({
  filterTransactions: z.boolean(),
  subscriptionProducts: z.enum(['yes', 'no']).optional(),
  sendRecurringOrders: z.enum(['send', 'block']).optional(),
  filterSalesChannel: z.array(z.string()).optional(),
  filterOrderTag: z.array(z.string()).optional(),
  filterCustomerTag: z.array(z.string()).optional(),
  filterOrderGateway: z.array(z.string()).optional(),
  filterAppId: z.array(z.string()).optional()
});
import { OperatorV2 } from '@/gql/graphql';

type TransactionsFormValues = z.infer<typeof transactionsFormSchema>;

export enum Destination {
  POSTSCRIPT = 'Postscript',
  KLAVIYO = 'Klaviyo',
  META = 'Meta',
  GA4 = 'GA4',
  SNAPCHAT = 'Snapchat',
  TIKTOK = 'TikTok',
  GOOGLEADS = 'GoogleAds'
}

export const TransactionsFilteringForm = ({
  isLastTab = true,
  onContinue,
  isSetup = false
}: {
  isLastTab?: boolean;
  onContinue?: () => void;
  isSetup?: boolean;
}) => {
  const { toast } = useToast();
  const client = useApolloClient();
  const { integration, isLoading, updatePropertyFilters } = useIntegrationStore();
  const params = useParams<{ id: string }>();

  const form = useForm<TransactionsFormValues>({
    resolver: zodResolver(transactionsFormSchema),
    defaultValues: {
      filterTransactions: false,
      subscriptionProducts: 'no',
      sendRecurringOrders: 'send',
      filterSalesChannel: [],
      filterOrderTag: [],
      filterCustomerTag: [],
      filterOrderGateway: [],
      filterAppId: []
    }
  });

  useEffect(() => {
    if (integration) {
      const ordersCreateFilter = integration.findFiltersV2.find(
        (filter) => filter.eventName === 'orders_create'
      );
      const propertyFilters = ordersCreateFilter?.propertyFilters ?? [];

      const isUserFilteringTransactions = propertyFilters.length > 0;

      const isUserBlockingRecurringOrders = propertyFilters.some(
        (propFilter) =>
          (propFilter.prop === 'source_name' &&
            SUBSCRIPTION_SALES_CHANNELS.map((ch) => ch.value).some((value) =>
              propFilter.value.includes(value)
            )) ||
          (propFilter.prop === 'tags' &&
            SUBSCRIPTION_ORDER_TAGS.map((tag) => tag.value).some((value) =>
              propFilter.value.includes(value)
            ))
      );

      form.reset({
        filterTransactions: isUserFilteringTransactions ? true : false,
        subscriptionProducts: isUserFilteringTransactions ? 'yes' : 'no',
        sendRecurringOrders: isUserBlockingRecurringOrders ? 'block' : 'send',
        filterSalesChannel: propertyFilters
          .filter((pf) => pf.prop === 'source_name')
          .flatMap((pf) => pf.value),
        filterOrderTag: propertyFilters
          .filter((pf) => pf.prop === 'tags')
          .flatMap((pf) => pf.value),
        filterCustomerTag: propertyFilters
          .filter((pf) => pf.prop === 'customer_tags')
          .flatMap((pf) => pf.value),
        filterOrderGateway: propertyFilters
          .filter((pf) => pf.prop === 'payment_gateway_names')
          .flatMap((pf) => pf.value),
        filterAppId: propertyFilters.filter((pf) => pf.prop === 'app_id').flatMap((pf) => pf.value)
      });
    }
  }, [integration, form.reset]);

  const { watch, control, setValue } = form;
  const filterTransactions = watch('filterTransactions');
  const subscriptionProducts = watch('subscriptionProducts');
  const sendRecurringOrders = watch('sendRecurringOrders');

  // Pin or release the subscription channels and tags when the recurring
  // orders answer changes.
  //
  // Read through getValues, never the watch() bindings above. The page mounts
  // this form only once the integration has loaded, so on a fresh load the
  // reset effect and this one run in the same commit: reset fills the form
  // with the stored filters and flips the answer to "block", and this effect
  // then fires with the closure of the first render -- answer "send", channel
  // list empty -- and writes that empty list over what reset just loaded. The
  // next render re-pins the two subscription values, so those two chips come
  // back, but every custom sales channel and order tag the merchant saved is
  // gone from the screen, and the next Save writes that truncated list back.
  // getValues sees the form as it is now, so reset's values survive.
  useEffect(() => {
    const mode = form.getValues('sendRecurringOrders');
    const channels = form.getValues('filterSalesChannel') ?? [];
    const tags = form.getValues('filterOrderTag') ?? [];
    const subscriptionChannels = SUBSCRIPTION_SALES_CHANNELS.map((ch) => ch.value);
    const subscriptionTags = SUBSCRIPTION_ORDER_TAGS.map((tag) => tag.value);

    if (mode === 'block') {
      setValue('filterSalesChannel', Array.from(new Set([...channels, ...subscriptionChannels])));
      setValue('filterOrderTag', Array.from(new Set([...tags, ...subscriptionTags])));
    } else {
      setValue(
        'filterSalesChannel',
        channels.filter((ch) => !subscriptionChannels.includes(ch))
      );
      setValue(
        'filterOrderTag',
        tags.filter((tag) => !subscriptionTags.includes(tag))
      );
    }
  }, [sendRecurringOrders, form, setValue]);

  const onSubmit = async (values: TransactionsFormValues) => {
    // Prepare the input array according to PropertyFilterInputV2 structure
    const propertyFilterInput: { prop: string; operator: OperatorV2; value: string[] }[] = [
      ...(values.filterSalesChannel?.map((v: string) => ({
        prop: 'source_name',
        operator: OperatorV2.Contains,
        value: [v]
      })) ?? []),
      ...(values.filterOrderTag?.map((v: string) => ({
        prop: 'tags',
        operator: OperatorV2.Contains,
        value: [v]
      })) ?? []),
      ...(values.filterCustomerTag?.map((v: string) => ({
        prop: 'customer_tags',
        operator: OperatorV2.Contains,
        value: [v]
      })) ?? []),
      ...(values.filterOrderGateway?.map((v: string) => ({
        prop: 'payment_gateway_names',
        operator: OperatorV2.Contains,
        value: [v]
      })) ?? []),
      ...(values.filterAppId?.map((v: string) => ({
        prop: 'app_id',
        operator: OperatorV2.Contains,
        value: [v]
      })) ?? [])
    ];

    await updatePropertyFilters(client, {
      id: params.id!,
      input: propertyFilterInput
    });

    toast({
      title: 'Form submitted',
      description: 'Your transaction settings have been saved.',
      variant: 'default'
    });

    if (!isLastTab && onContinue) {
      onContinue();
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit(onSubmit)(e);
        }}
      >
        <FormLayout>
          <FormSection>
            <SectionHeader>Filter Transactions</SectionHeader>

            <ToggleWithDescription form={form} name="filterTransactions">
              You can choose to filter orders when sending data to this destination. For example,
              you might want to only allow web channel orders or block all point of sale orders.
            </ToggleWithDescription>
          </FormSection>
          {filterTransactions && (
            <FormSection>
              <SectionHeader>Do you sell subscription products?</SectionHeader>
              <SectionParagraph>
                Subscription products are products you sell on a recurring basis (recurring orders).
                You may have installed subscription apps like ReCharge, OrderGroove, etc.
              </SectionParagraph>
              <RadioField
                form={form}
                name="subscriptionProducts"
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' }
                ]}
              />
            </FormSection>
          )}
          {filterTransactions && subscriptionProducts === 'yes' && (
            <FormSection>
              <SectionHeader>Would you like to send recurring orders?</SectionHeader>
              <SectionParagraph>
                Recurring orders are the scheduled repeating purchases that your shopper has
                subscribed to. You can choose to send recurring orders to this destination or to
                block them. has subscribed to.
              </SectionParagraph>
              <RadioField
                form={form}
                name="sendRecurringOrders"
                options={[
                  { value: 'send', label: 'Yes, send' },
                  { value: 'block', label: 'No, block' }
                ]}
              />
            </FormSection>
          )}
          {filterTransactions && (
            <FormSection>
              <SectionHeader>Filter Transactions</SectionHeader>
              <FormField
                control={control}
                name="filterSalesChannel"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="font-[500]">Sales Channel</FormLabel>
                    <FormControl>
                      <MultiSelect
                        options={[
                          ...SALES_CHANNEL_OPTIONS,
                          ...(field.value ?? [])
                            .filter((ch) => !SALES_CHANNEL_OPTIONS.some((opt) => opt.value === ch))
                            .map((ch) => ({ label: ch, value: ch }))
                        ]}
                        onValueChange={(value) => {
                          // Ensure fixed values are always included
                          if (sendRecurringOrders === 'block') {
                            const updatedValue = Array.from(
                              new Set([
                                ...value,
                                ...SUBSCRIPTION_SALES_CHANNELS.map((ch) => ch.value)
                              ])
                            );
                            setValue('filterSalesChannel', updatedValue);
                            field.onChange(updatedValue);
                          } else {
                            field.onChange(value);
                          }
                        }}
                        value={field.value}
                        placeholder="Select sales channels to filter"
                        fixedValues={
                          sendRecurringOrders === 'block' ? SUBSCRIPTION_SALES_CHANNELS : []
                        }
                        variant="inverted"
                        animation={2}
                        maxCount={1}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Filter Type: Order Tag */}
              <FormField
                control={control}
                name="filterOrderTag"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="font-[500]">Order Tag</FormLabel>
                    <FormControl>
                      <MultiSelect
                        options={[
                          ...ORDER_TAG_OPTIONS,
                          ...(field.value ?? [])
                            .filter((tag) => !ORDER_TAG_OPTIONS.some((opt) => opt.value === tag))
                            .map((tag) => ({ label: tag, value: tag }))
                        ]}
                        onValueChange={(value) => {
                          // Ensure fixed values are always included
                          if (sendRecurringOrders === 'block') {
                            const updatedValue = Array.from(
                              new Set([
                                ...value,
                                ...SUBSCRIPTION_ORDER_TAGS.map((tag) => tag.value)
                              ])
                            );
                            setValue('filterOrderTag', updatedValue);
                            field.onChange(updatedValue);
                          } else {
                            field.onChange(value);
                          }
                        }}
                        value={field.value}
                        placeholder="Select order tags to filter"
                        fixedValues={sendRecurringOrders === 'block' ? SUBSCRIPTION_ORDER_TAGS : []}
                        variant="inverted"
                        animation={2}
                        maxCount={1}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Filter Type: Customer Tag */}
              <FormField
                control={control}
                name="filterCustomerTag"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="font-[500]">Customer Tag</FormLabel>
                    <FormControl>
                      <MultiSelect
                        options={(field.value ?? []).map((tag: string) => ({
                          label: tag,
                          value: tag
                        }))}
                        onValueChange={field.onChange}
                        value={field.value}
                        placeholder="Select customer tags to filter"
                        variant="inverted"
                        animation={2}
                        maxCount={1}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* App ID Filter */}
              <FormField
                control={control}
                name="filterAppId"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="font-[500]">App ID</FormLabel>
                    <FormControl>
                      <MultiSelect
                        options={(field.value ?? []).map((appId: string) => ({
                          label: appId,
                          value: appId
                        }))}
                        onValueChange={field.onChange}
                        value={field.value}
                        placeholder="Select app IDs to filter"
                        variant="inverted"
                        animation={2}
                        maxCount={1}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Order Gateway Filter */}
              <FormField
                control={control}
                name="filterOrderGateway"
                render={({ field }) => (
                  <FormItem className="w-1/2">
                    <FormLabel className="font-[500]">Order Gateway</FormLabel>
                    <FormControl>
                      <MultiSelect
                        options={(field.value ?? []).map((gateway: string) => ({
                          label: gateway,
                          value: gateway
                        }))}
                        onValueChange={field.onChange}
                        value={field.value}
                        placeholder="Select order gateways to filter"
                        variant="inverted"
                        animation={2}
                        maxCount={1}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>
          )}
          <FormSection>
            <SaveButton
              isDirty={form.formState.isDirty}
              isUpdating={isLoading}
              isInitialSetup={isSetup}
            />
          </FormSection>
        </FormLayout>
      </form>
    </Form>
  );
};

export default TransactionsFilteringForm;
